// src/components/PaymentDetails.tsx
import { Box, Flex, Text, Skeleton } from '@chakra-ui/react';
import { useMemo } from 'react';

import { getCurrencySymbol } from '@/utils/currencyUtils';
import TransactionHistory from './TransactionHistory';
import { useGetVendorPaymentHistory } from '@/services/payment';
import { useGetVendorRemainingPayment } from '@/services/payment'; // ← Import the new hook

const PaymentDetails: React.FC = () => {
  // Keep existing history hook
  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError,
  } = useGetVendorPaymentHistory({
    page: 1,
    page_size: 10,
  });

  // New hook for due amount
  const {
    data: remainingData,
    isLoading: remainingLoading,
    isError: remainingError,
  } = useGetVendorRemainingPayment();

  // Compute values
  const { dueAmount, currency, transactionData } = useMemo(() => {
    const due = remainingData?.remaining_amount || 0;
    const curr = remainingData?.currency || 'USD';

    const results = historyData?.results || [];

    return {
      dueAmount: due,
      currency: curr,
      transactionData: { results },
    };
  }, [remainingData, historyData]);

  // Loading/error state for the due amount display
  const isDueLoading = remainingLoading;
  const isDueError = remainingError;

  return (
    <Box fontFamily="primary" p={6}>
      <Box mb={10}>
        <Box
          borderColor="border.default"
          borderWidth="1px"
          py={3}
          px={4}
          borderRadius="lg"
          overflow="hidden"
          bg="#FFFFFF"
        >
          <Flex justify="space-between" align="center">
            <Flex align="center" gap={1}>
              <Text size="md" fontWeight="semibold">
                Payment Due -
              </Text>
              {isDueLoading || isDueError ? (
                <Skeleton
                  height="20px"
                  width={{ base: '80px', sm: '100px', md: '120px' }}
                  startColor="button.disable"
                  endColor="button.disable"
                />
              ) : (
                <Text fontSize="20px" fontWeight="semibold">
                  {getCurrencySymbol(currency)}
                  {dueAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Box mb={4}>
        <Text fontSize="xl" fontWeight="semibold" mb={6}>
          Transaction History
        </Text>
        <TransactionHistory
          isLoading={historyLoading}
          isError={historyError}
          transactionData={transactionData}
        />
      </Box>
    </Box>
  );
};

export default PaymentDetails;
